import { RadioGroup, Typography } from '@mui/material';
import type { FC } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { VrsCallCommentsHelperText } from 'features/call/vrs-call-comments/components';
import type { BugReportReason } from 'features/call/vrs-call-comments/enums';
import type { VrsCallCommentsBugReportForm } from 'features/call/vrs-call-comments/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { FormControlLabel, Radio } from 'features/ui/elements';

export interface VrsCallCommentsReasonControlProps {
  control: Control<VrsCallCommentsBugReportForm>;
  controlName: BugReportReason;
  reasons: string[];
  reasonTitle: string;
}

export const VrsCallCommentsBugReportReasonControl: FC<
  VrsCallCommentsReasonControlProps
> = ({ control, controlName, reasons, reasonTitle }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'bugReport.reason',
  });
  const dataTestId = 'vrs-call-comments-bug-report-reason';

  return (
    <>
      <Typography
        variant='h2'
        color={theme.palette.common.white}
        mt={12}
        data-testid={`${dataTestId}-title`}
      >
        {reasonTitle}
      </Typography>
      <Typography
        variant='subtitle2'
        color={theme.palette.grey[300]}
        mt={12}
        data-testid={`${dataTestId}-subtitle`}
      >
        {t('reasonSubtitle')}
      </Typography>
      <Controller
        control={control}
        name={controlName}
        rules={{ required: true }}
        render={({ field: { ...args }, fieldState }) => (
          <>
            <RadioGroup {...args} data-testid={`${dataTestId}-radio-group`}>
              {reasons.map((reason: string) => (
                <RadioControlLabel
                  key={reason}
                  value={reason}
                  label={t(reason)}
                  control={<Radio />}
                />
              ))}
            </RadioGroup>
            {fieldState.error && (
              <VrsCallCommentsHelperText
                data-testid={`${dataTestId}-helper-text`}
              >
                {t('bugReportValidation')}
              </VrsCallCommentsHelperText>
            )}
          </>
        )}
      />
    </>
  );
};

const RadioControlLabel = styled(FormControlLabel)`
  margin-top: ${theme.spacing(4)};
`;
